import { NextPageContext } from 'next';
import { LngLat } from 'maplibre-gl';
import { DateRange } from 'react-day-picker';
import { apiCall } from '@/utils/api';
import { IAuditGNSS } from '@/types/admin';
import { GeoJSONFeature } from '@/types/geojson';
import { ClassLabel } from '@/types/label';
import {
  ILandmark,
  IObjectDetection,
  LandmarkHistoryEvent,
} from '@/types/features';

export const getGnssData = async (
  id: string,
  req?: NextPageContext['req'],
): Promise<IAuditGNSS> => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/auditgnss/${id}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getLandmark = async (
  id: string,
  req?: NextPageContext['req'],
): Promise<ILandmark | { error: string }> => {
  try {
    const URL = `/admin/landmark/${id}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching landmark info: ', error);
    return { error };
  }
};

/** Note: Need to try and catch error at the callsite unless using useSWR */
export const getLandmarkHistory = async (
  id: string,
  req?: NextPageContext['req'],
): Promise<LandmarkHistoryEvent[]> => {
  try {
    const URL = `/landmarks/${id}/history/v2`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching landmark history: ', error);
    throw new Error(error);
  }
};

export const getLandmarkPair = async (
  id: string,
  req?: NextPageContext['req'],
) => {
  try {
    const URL = `/admin/landmarkpair/${id}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching landmark pair info: ', error);
    return { error };
  }
};

export const getMapFeaturePair = async (
  id: string,
  req?: NextPageContext['req'],
) => {
  try {
    const URL = `/admin/mapfeaturepair/${id}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching landmark pair info: ', error);
    return { error };
  }
};

/** Note: Need to try and catch error at the callsite unless using useSWR */
export const getMapFeatureSwarmConsistency = async (
  id: string,
  req?: NextPageContext['req'],
): Promise<{ swarmConsistency: boolean }> => {
  try {
    const URL = `/mapfeatures/${id}/swarmConsistency`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error) {
    console.log('Error fetching map feature swarm consistency info: ', error);
    throw new Error(error as any);
  }
};

export const getDetectionArea = async (id: string) => {
  try {
    const URL = `/admin/detectionarea/${id}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching detection area info: ', error);
    return { error };
  }
};

export const getDetectionAreaAssets = async (id: string) => {
  try {
    const URL = `/admin/detectionarea/${id}/assets`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching detection area assets: ', error);
    return { error };
  }
};

export const getObjectDetection = async (
  id: string,
  req?: NextPageContext['req'],
): Promise<{ objectDetection: IObjectDetection } | { error: string }> => {
  try {
    const URL = `/objectdetections/${id}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching objectdetection info: ', error);
    return { error };
  }
};

export const getObjectDetectionLandmark = async (
  id: string,
): Promise<{ landmark: ILandmark } | { error: string }> => {
  try {
    const URL = `/objectdetections/${id}/landmark`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
    });

    return res;
  } catch (error: any) {
    console.log('Error fetching objectdetection info: ', error);
    return { error };
  }
};

export const getDashcamFrame = async (
  id: string,
  req?: NextPageContext['req'],
) => {
  try {
    const URL = `/admin/dashcamframe/${id}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching dashcam frame info: ', error);
    return { error };
  }
};

export const getMapFeatureCandidate = async (
  id: string,
  req?: NextPageContext['req'],
) => {
  try {
    const URL = `/mapfeaturecandidates/${id}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching mapfeaturecandidate info: ', error);
    return { error };
  }
};

export const getDetectionInfo = async (
  frameId: string,
  detectionId: number,
  req?: NextPageContext['req'],
) => {
  try {
    const URL = `/admin/frame/${frameId}/${detectionId}`;
    const res = await apiCall(`${process.env.NEXT_PUBLIC_API}${URL}`, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return res;
  } catch (error: any) {
    console.log('Error fetching detection info: ', error);
    return { error };
  }
};

export const getTrackedIntersectionInfo = async (
  id: string,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/tracked-intersection/${id}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
      headers: {
        cookie: req?.headers.cookie,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getCoverageReports = async (
  geometry: GeoJSONFeature,
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/coveragereports`;
  try {
    const response = await apiCall(url, {
      method: 'POST',
      headers: {
        cookie: req?.headers.cookie,
      },
      body: JSON.stringify({ geometry }),
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const enqueueReconstruction = async (
  coverageReports: string[],
  req?: NextPageContext['req'],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/enqueueReconstruction`;
  try {
    const response = await apiCall(url, {
      method: 'POST',
      headers: {
        cookie: req?.headers.cookie,
      },
      body: JSON.stringify({ coverageReports }),
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getImageQualityTestPageConfig = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/image-quality-test-page-config`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getPhiVisionExperimentConfigs = async (label: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/phi-vision-experiment/${label}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getRegionConfigurationUpdateRecordForKeys = async (
  keys: string[],
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/regionConfigurationUpdateRecord`;
  try {
    const response = await apiCall(url, {
      method: 'POST',
      body: JSON.stringify({ keys }),
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getClosestConnectorOrSegmentImagery = async ({
  lng,
  lat,
}: LngLat) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/coverage/${lng},${lat}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    return { error };
  }
};

export const getConstructionDataUrl = async () => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/getLatestConstructionEvalData`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getAdminObjectUrl = async (id: string) => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/search/${id}`;
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getLandmarks = async ({
  confidence,
  date,
  label,
  limit,
  offset = 0,
}: {
  confidence: number[];
  date: DateRange | undefined;
  label: ClassLabel;
  limit?: number;
  offset?: number;
}) => {
  const url =
    `${process.env.NEXT_PUBLIC_API}/landmarks?` +
    [
      `minConfidence=${confidence[0]}`,
      `maxConfidence=${confidence[1]}`,
      `startDate=${date?.from}`,
      `endDate=${date?.to}`,
      `label=${label}`,
      `limit=${limit}`,
      `offset=${offset}`,
    ].join('&');
  try {
    const response = await apiCall(url, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const sendLandmarksToAI = async ({
  customPrompt,
  imageUrls,
  query,
  responseSchema,
}: {
  customPrompt: string;
  imageUrls?: string[];
  query: {
    minConfidence: number;
    maxConfidence: number;
    startDate: Date;
    endDate: Date;
    label: ClassLabel;
    limit: number;
    offset: number;
  };
  responseSchema?: Record<string, any>;
}) => {
  const url = `${process.env.NEXT_PUBLIC_API}/landmarks/submitToAI`;
  try {
    const response = await apiCall(url, {
      method: 'POST',
      body: JSON.stringify({
        query,
        ...(customPrompt && customPrompt.length > 0 ? { customPrompt } : {}),
        ...(imageUrls && imageUrls.length > 0 ? { imageUrls } : {}),
        responseSchema,
      }),
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const removeObjectDetectionsFromLandmark = async (
  objectDetectionIds: string[],
  createNewLandmark: boolean,
) => {
  const url = `${process.env.NEXT_PUBLIC_API}/landmarks/removeObjectDetections`;
  try {
    const response = await apiCall(url, {
      method: 'POST',
      body: JSON.stringify({
        objectDetectionIds,
        createNewLandmark,
      }),
    });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};

export const getSignedUrlsForUpload = async ({
  keys,
}: {
  keys: string[];
}): Promise<{
  error?: string;
  urls?: Record<string, { key: string; putUrl: string; getUrl: string }>;
}> => {
  const url = `${process.env.NEXT_PUBLIC_API}/admin/getSignedUrlsForUpload`;
  try {
    const response = await apiCall(url, { method: 'POST' }, { keys });
    return response;
  } catch (error: any) {
    console.log(error);
    return { error };
  }
};
